import { EntityType } from '@nkr/enums/entity-type';
import { IAllowDuplicateOverridePost } from '@nkr/models/allow-duplicate/allow-duplicate-override';
import { IBaseResponse } from '@nkr/models/index';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { useEffect, useState } from 'react';

interface IRetVal {
    isAllowed: boolean,
    loading: boolean,
    error: string,
    allow: (reason: string) => Promise<void>,
    revoke: (reason: string) => Promise<void>
}

function useAllowDuplicateOverride(entityType: EntityType, profileId: number): IRetVal {
    const { dispatch } = useAppHookContext();

    const [isAllowed, setIsAllowed] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    // The API returns a specific message for expected failures (missing reason, unknown profile,
    // unsupported entity type) via UserException -> BadRequest. Prefer it over generic text.
    const messageFor = (err: any, fallback: string): string => {
        return err?.response?.data ? err.response.data : fallback;
    };

    const load = async () => {
        if (!profileId) {
            return;
        }

        setError('');
        setLoading(true);

        let options = {
            url: `${__API__}/admin/allow-duplicate/${entityType}/${profileId}`
        };

        try {
            const r: IBaseResponse = await dispatch(AuthActionCreators.apiGet(options));

            if (r.data.success) {
                setIsAllowed(r.data.results.isAllowed);
            } else {
                setError('An error occurred while loading the allow duplicate setting.');
            }
        } catch (err) {
            setError(messageFor(err, 'An error occurred while loading the allow duplicate setting.'));
        } finally {
            setLoading(false);
        }
    };

    const save = async (action: string, reason: string) => {
        setError('');
        setLoading(true);

        let data: IAllowDuplicateOverridePost = { entityType, profileId, reason };

        let options = {
            url: `${__API__}/admin/allow-duplicate/${action}`,
            data
        };

        try {
            const r: IBaseResponse = await dispatch(AuthActionCreators.apiPost(options));

            if (r.data.success) {
                await load();
            } else {
                setError('An error occurred while saving the allow duplicate setting.');
            }
        } catch (err) {
            setError(messageFor(err, 'An error occurred while saving the allow duplicate setting.'));
        } finally {
            setLoading(false);
        }
    };

    const allow = (reason: string) => save('allow', reason);

    const revoke = (reason: string) => save('revoke', reason);

    useEffect(() => {
        load();
    }, [entityType, profileId]);

    return { isAllowed, loading, error, allow, revoke }
}

export default useAllowDuplicateOverride;
